import { NavLink, useParams } from "react-router";
import { useHtzReportShowQuery } from "@/services/backend/htz/report/api/report-api";
import { Report } from "@/services/backend/htz/report/types/report";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import t from "@/lang/lang";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import {
  ErrDocumentNotReadyContextProvider,
  useErrDocumentNotReady,
} from "@/shared/bsm/core/document/components/ui/err-document-not-ready";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { DeleteReportDialog } from "@/routes/processes/[processId]/htz/reports/[reportId]/_components/delete-report-dialog";
import { ReportPDF } from "@/routes/processes/[processId]/htz/reports/[reportId]/_components/report-pdf";
import { hasFieldError } from "@/shared/lib/errors/validation-error";
import {
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
  DocumentPageNavigation,
  DocumentPageNavigationLink,
  DocumentPageNavigationOutlet,
  DocumentPageSkeleton,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { DocumentImmutableAlert } from "@/shared/bsm/core/document/components/ui/document-immutable-alert";
import {
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/shared/components/ui/breadcrumb";
import { ProcessPageMainTemplate } from "@/routes/processes/[processId]/_components/process-page-main-template";
import { HtzBreadcrumbs } from "@/routes/processes/[processId]/htz/_components/htz-breadcrumbs";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import { CpyDialog } from "@/services/backend/cpy/components/cpy-dialog";
import { CpyName } from "@/services/backend/cpy/types/entity-names";

export function ReportRoute() {
  const { reportId } = useParams();

  const {
    data: report,
    isLoading,
    error,
  } = useHtzReportShowQuery({
    id: reportId!,
  });

  const breadcrumbs = report && (
    <HtzBreadcrumbs
      customerId={report.customerId}
      processId={report.processId!}
    >
      <BreadcrumbSeparator />
      <BreadcrumbItem className="hidden md:block">
        <BreadcrumbLink asChild>
          <NavLink to={`/processes/htz/${report.processId!}/reports`}>
            {t("Berichte")}
          </NavLink>
        </BreadcrumbLink>
      </BreadcrumbItem>
      <BreadcrumbSeparator />
      <BreadcrumbItem>
        <BreadcrumbPage>
          <DocumentNumberById documentId={report.id} />
        </BreadcrumbPage>
      </BreadcrumbItem>
    </HtzBreadcrumbs>
  );

  return (
    <ProcessPageMainTemplate breadcrumbs={breadcrumbs}>
      {error && <RTKQueryErrorAlert error={error} />}
      {isLoading && <DocumentPageSkeleton />}
      {report && (
        <PersistenceContextProvider>
          <ErrDocumentNotReadyContextProvider>
            <ReportPage report={report} />
          </ErrDocumentNotReadyContextProvider>
        </PersistenceContextProvider>
      )}
    </ProcessPageMainTemplate>
  );
}

function ReportPage({ report }: { report: Report }) {
  const { notReadyError } = useErrDocumentNotReady();

  const dataErr = hasFieldError(
    notReadyError,
    "date",
    "createdBy",
    "objectAddress",
    "createdFor",
    "inspectionDate",
    "inspectionConductedBy",
    "inspectionConductedByStaff",
    "lastInspectionDate",
    "nextInspectionDate",
    "VDI6022",
    "representative",
  );
  const itemErr = hasFieldError(
    notReadyError,
    "item",
    "ahuImagePrefix",
    "ahuImageFileID",
  );
  const microbialErr = hasFieldError(notReadyError, "microbial");
  const structuralErr = hasFieldError(notReadyError, "structural");
  const visualErr = hasFieldError(notReadyError, "visual");
  const photosErr = hasFieldError(notReadyError, "photo");

  const links = [
    { to: "data", label: t("Bericht"), hasValidationError: dataErr },
    { to: "items", label: t("Komponenten"), hasValidationError: itemErr },
    {
      to: "microbial",
      label: t("Mikrobiologisch"),
      hasValidationError: microbialErr,
    },
    {
      to: "structural",
      label: t("Konstruktiv"),
      hasValidationError: structuralErr,
    },
    { to: "visual", label: t("Visuell"), hasValidationError: visualErr },
    { to: "photos", label: t("Fotos"), hasValidationError: photosErr },
  ];

  return (
    <DocumentPage>
      <DocumentImmutableAlert document={report} />
      <DocumentPageHeader>
        <DocumentPageNavigation>
          {links.map((link) => (
            <DocumentPageNavigationLink
              key={link.to}
              to={link.to}
              hasValidationError={link.hasValidationError}
            >
              {link.label}
            </DocumentPageNavigationLink>
          ))}
        </DocumentPageNavigation>
        <DocumentPageActions>
          {!report?.immutable && (
            <>
              <PersistenceStateUsingContext className="mr-2" />
              <CpyDialog
                destinationEntityName={CpyName.HTZReport}
                destinationId={report.id}
                description={t(
                  "Es können Daten aus Positionen von Arbeitsscheinen aus diesem Vorgang übernommen werden.",
                )}
                initialParams={{
                  process: { active: true, values: [report.processId!] },
                }}
              />
              <DeleteReportDialog reportId={report.id} />
            </>
          )}
          <ReportPDF report={report} />
        </DocumentPageActions>
      </DocumentPageHeader>
      <DocumentPageContent>
        <DocumentPageNavigationOutlet />
      </DocumentPageContent>
    </DocumentPage>
  );
}
